import { useState } from "react";
import type { CreatePoolFormState, PoolMode, ReClammParams } from "../types/pool";
import "./CreatePoolForm.css";

const DEFAULT_RECLAMM: ReClammParams = {
  marginPct: 80,
  exponentPct: 50,
  sigmaPct: 5,
  autoGlide: false,
};

const FEE_OPTIONS = [
  { label: "0.01%", value: 0.01 },
  { label: "0.05%", value: 0.05 },
  { label: "0.3%", value: 0.3 },
  { label: "1%", value: 1 },
  { label: "10%", value: 10 },
];

/** Governance fee ($MORM) for local pools (Round A). */
const LOCAL_POOL_GOV_FEE = 10;
/** Governance fee ($MORM) for oracle-backed synthetic pools (Round B). */
const ORACLE_POOL_GOV_FEE = 910;

/** Maximum rebate % at the highest volume tier (cap 99%). */
const MAX_REBATE_PCT = 99;

const VOLUME_TIERS: { label: string }[] = [
  { label: "0 – 10k" },
  { label: "10k – 100k" },
  { label: "100k – 1M" },
  { label: "1M – 10M" },
  { label: "10M+" },
];

/** Rebate % at tier index i (0..n-1), fairly distributed from 0% to maxRebatePct. */
function rebateAtTier(i: number, numTiers: number, maxRebatePct: number): number {
  if (numTiers <= 1) return i === 0 ? 0 : maxRebatePct;
  return Math.round((i / (numTiers - 1)) * maxRebatePct);
}

interface CreatePoolFormProps {
  initial?: Partial<CreatePoolFormState>;
  onSubmit: (form: CreatePoolFormState) => void;
  onBack: () => void;
}

export function CreatePoolForm({
  initial,
  onSubmit,
  onBack,
}: CreatePoolFormProps) {
  const [tokenA, setTokenA] = useState(initial?.tokenA ?? "USDC");
  const [tokenB, setTokenB] = useState(initial?.tokenB ?? "wETH");
  const [poolMode, setPoolMode] = useState<PoolMode>(
    initial?.poolMode ?? "Local"
  );
  const [pythPriceFeedId, setPythPriceFeedId] = useState(
    initial?.pythPriceFeedId ?? ""
  );
  const [feeTierPct, setFeeTierPct] = useState(initial?.feeTierPct ?? 0.3);
  const [feeRebateEnabled, setFeeRebateEnabled] = useState(
    initial?.feeRebatePct != null && initial.feeRebatePct > 0
  );
  const [maxRebatePct, setMaxRebatePct] = useState(() => {
    const v = initial?.feeRebatePct;
    if (v != null && v > 0) return Math.min(MAX_REBATE_PCT, Math.max(0, v));
    return 20;
  });
  const [tickSpacing, setTickSpacing] = useState(initial?.tickSpacing ?? 60);
  const [reclamm, setReclamm] = useState<ReClammParams>(
    initial?.reclamm ?? DEFAULT_RECLAMM
  );
  const [boosted, setBoosted] = useState(initial?.boosted ?? false);
  const [efficiency] = useState(() => Math.round(180 + Math.random() * 80));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      tokenA,
      tokenB,
      poolMode,
      ...(poolMode === "SyntheticOracleBacked" ? { pythPriceFeedId } : {}),
      feeTierPct,
      ...(feeRebateEnabled
        ? { feeRebatePct: Math.min(MAX_REBATE_PCT, Math.max(0, maxRebatePct)) }
        : {}),
      tickSpacing,
      reclamm,
      boosted,
    });
  };

  return (
    <div className="create-pool-form screen">
      <header className="form-header">
        <button type="button" className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <h1>Create New Pool</h1>
      </header>

      <form className="create-pool-form-inner" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Token A</label>
            <input
              type="text"
              value={tokenA}
              onChange={(e) => setTokenA(e.target.value)}
              placeholder="e.g. USDC"
            />
          </div>
          <div className="form-group">
            <label>Token B</label>
            <input
              type="text"
              value={tokenB}
              onChange={(e) => setTokenB(e.target.value)}
              placeholder="e.g. wETH"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Pool Mode</label>
          <div className="toggle-group">
            <button
              type="button"
              className={poolMode === "Local" ? "active" : ""}
              onClick={() => setPoolMode("Local")}
            >
              Local
            </button>
            <button
              type="button"
              className={poolMode === "SyntheticOracleBacked" ? "active" : ""}
              onClick={() => setPoolMode("SyntheticOracleBacked")}
            >
              Synthetic (Oracle)
            </button>
          </div>
        </div>

        {poolMode === "SyntheticOracleBacked" && (
          <div className="form-group pyth-feed-group">
            <label htmlFor="pyth-price-feed-id">Pyth Price Feed ID</label>
            <input
              id="pyth-price-feed-id"
              type="text"
              value={pythPriceFeedId}
              onChange={(e) => setPythPriceFeedId(e.target.value)}
              placeholder="e.g. 0x..."
            />
            <p className="pyth-feed-hint">
              Find price feed IDs in{" "}
              <a
                href="https://docs.pyth.network/price-feeds/pro/price-feed-ids"
                target="_blank"
                rel="noopener noreferrer"
              >
                Pyth Pro Price Feed IDs
              </a>
              .
            </p>
          </div>
        )}

        <div className="form-group">
          <label>Fee Tier</label>
          <select
            value={feeTierPct}
            onChange={(e) => setFeeTierPct(Number(e.target.value))}
          >
            {FEE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group fee-rebate-group">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={feeRebateEnabled}
              onChange={(e) => setFeeRebateEnabled(e.target.checked)}
            />
            Enable volume-tier fee rebate
          </label>
          {feeRebateEnabled && (
            <div className="fee-rebate-options">
              <p className="fee-rebate-explanation">
                Rebate is based on trading volume (USD). It is{" "}
                <strong>fairly distributed</strong> across tiers: from 0% at
                the lowest tier up to your chosen max at the highest tier (max{" "}
                {MAX_REBATE_PCT}%).
              </p>
              <div className="form-group">
                <label htmlFor="max-rebate-pct">
                  Max rebate % (highest tier)
                </label>
                <input
                  id="max-rebate-pct"
                  type="number"
                  min={0}
                  max={MAX_REBATE_PCT}
                  value={maxRebatePct}
                  onChange={(e) =>
                    setMaxRebatePct(
                      Math.min(
                        MAX_REBATE_PCT,
                        Math.max(0, Number(e.target.value) || 0)
                      )
                    )
                  }
                />
              </div>
              <div className="volume-tier-table-wrap">
                <table
                  className="volume-tier-table"
                  aria-label="Volume tier rebate schedule"
                >
                  <thead>
                    <tr>
                      <th>Volume tier (USD)</th>
                      <th>Rebate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VOLUME_TIERS.map((tier, i) => (
                      <tr key={tier.label}>
                        <td>{tier.label}</td>
                        <td>
                          {rebateAtTier(
                            i,
                            VOLUME_TIERS.length,
                            maxRebatePct
                          )}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="fee-rebate-settlement">
                Rebate is settled <strong>once every 2 weeks</strong>.
              </p>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Tick Spacing</label>
          <input
            type="number"
            min={1}
            value={tickSpacing}
            onChange={(e) => setTickSpacing(Number(e.target.value) || 60)}
          />
        </div>

        <div className="form-group reclamm-section">
          <label>ReClamm Params</label>
          <p className="reclamm-explanation">
            ReClamm (Reinforced Concentrated Liquidity AMM) shapes the pool’s virtual curve so liquidity earns more while staying concentrated. Tune these to balance capital efficiency and impermanent loss.
          </p>
          <div className="reclamm-params">
            <div className="reclamm-param">
              <label htmlFor="reclamm-margin">Margin %</label>
              <input
                id="reclamm-margin"
                type="number"
                min={0}
                max={100}
                value={reclamm.marginPct}
                onChange={(e) =>
                  setReclamm((r) => ({ ...r, marginPct: Number(e.target.value) || 0 }))
                }
              />
              <span className="param-hint">Share of virtual reserves vs real (higher = more efficiency, more IL).</span>
            </div>
            <div className="reclamm-param">
              <label htmlFor="reclamm-exponent">Exponent %</label>
              <input
                id="reclamm-exponent"
                type="number"
                min={0}
                max={100}
                value={reclamm.exponentPct}
                onChange={(e) =>
                  setReclamm((r) => ({
                    ...r,
                    exponentPct: Number(e.target.value) || 0,
                  }))
                }
              />
              <span className="param-hint">Curve curvature (affects price impact shape).</span>
            </div>
            <div className="reclamm-param">
              <label htmlFor="reclamm-sigma">Sigma %</label>
              <input
                id="reclamm-sigma"
                type="number"
                min={0}
                max={100}
                value={reclamm.sigmaPct}
                onChange={(e) =>
                  setReclamm((r) => ({ ...r, sigmaPct: Number(e.target.value) || 0 }))
                }
              />
              <span className="param-hint">Volatility / spread around current price.</span>
            </div>
          </div>
          <div className="reclamm-advanced">
            <span className="advanced-badge">Advanced</span>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={reclamm.autoGlide ?? false}
                onChange={(e) =>
                  setReclamm((r) => ({ ...r, autoGlide: e.target.checked }))
                }
              />
              Auto Glide — virtual curve follows price automatically for maximum capital efficiency (recommended for experienced LPs).
            </label>
          </div>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={boosted}
              onChange={(e) => setBoosted(e.target.checked)}
            />
            Boosted Pool (100% utilization)
          </label>
        </div>

        <div className="form-preview">
          <p>
            Efficiency ~{efficiency}× · Governance Fee:{" "}
            {poolMode === "SyntheticOracleBacked"
              ? `${ORACLE_POOL_GOV_FEE}`
              : `${LOCAL_POOL_GOV_FEE}`}{" "}
            $MORM
          </p>
        </div>

        <div className="form-actions">
          <button type="button" className="secondary" onClick={onBack}>
            Cancel
          </button>
          <button type="submit" className="primary">
            Submit Proposal
          </button>
        </div>
      </form>
    </div>
  );
}
